import { registerDestructor } from '@ember/destroyable';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import UiClassicLoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import UiClassicMessageBox from 'garaje/components/ui/classic/message-box';
import UiButton from 'garaje/components/ui-button';
import config from 'garaje/config/environment';
import iFrameResize from 'garaje/modifiers/iframe-resize';
import registerMessageSender from 'garaje/modifiers/register-message-sender';
import type EmbeddedAppService from 'garaje/services/embedded-app';
import type InactivityService from 'garaje/services/inactivity';
import type MessageBusService from 'garaje/services/message-bus';
import type WindowLocationService from 'garaje/services/window-location';
import { IFRAME_STATE } from 'garaje/utils/enums';
import { equal } from 'macro-decorators';

type IFrameState = (typeof IFRAME_STATE)[keyof typeof IFRAME_STATE];

interface IframeMessage {
  event: string;
  url?: string;
  context?: unknown;
  [key: string]: unknown;
}

interface IframeResizeData {
  height: string;
  [key: string]: unknown;
}

interface MessageData {
  iframe: HTMLIFrameElement;
  message: IframeMessage;
}

interface EmbeddedAppSignature {
  Args: {
    /** Optional context passed to iframe */
    context?: unknown;
    /** Name used for events emitted via messageBus service @default "embedded-app-message" */
    eventName?: string;
    /** Value to use as `id` attribute of iframe element */
    id: string;
    /** CSS class names to apply to iframe element */
    iframeClass?: string;
    /** Time (in ms) to wait before considering that loading iframe content has timed out @default 30000 */
    loadingTimeout?: number;
    /** Iframe resizer options */
    options?: object;
    /** Time (in ms) to wait before considering that iframe resizing has timed out @default 1000 */
    resizeTimeout?: number;
    /** Whether scrolling should be enabled on the iframe @default false */
    scrolling?: boolean;
    /** URL for iframe's `src` attribute */
    url: string;
  };
  Element: HTMLDivElement;
}

/**
 * EmbeddedApp component for displaying iframe content with loading states and message handling
 */
export default class EmbeddedApp extends Component<EmbeddedAppSignature> {
  @service declare embeddedApp: EmbeddedAppService;
  @service declare inactivity: InactivityService;
  @service declare messageBus: MessageBusService;
  @service declare router: RouterService;
  @service declare windowLocation: WindowLocationService;

  // states: loading, timeout, loaded
  @tracked state: IFrameState = IFRAME_STATE.LOADING;
  @equal('state', IFRAME_STATE.LOADING) declare isLoading: boolean;
  @equal('state', IFRAME_STATE.TIMEOUT) declare isTimedOut: boolean;
  @equal('state', IFRAME_STATE.LOADED) declare isLoaded: boolean;

  // Used to display a warning that the iframe is not set up correctly
  @tracked showIframeWarning = false;

  // Used to cache bust the iframe URL
  @tracked timestamp = Date.now();

  // Message sender function for communicating with iframe
  // make the sender optional rather than using a non-null assertion
  messageSender?: (message: unknown) => void;

  // ec on('init') doesn't seem to fire in a glimmer component. manually performing the
  // task on component init
  constructor(owner: unknown, args: EmbeddedAppSignature['Args']) {
    super(owner, args);
    void this.loadingIframeTask.perform();
  }

  /**
   * Add a timestamp query param to the URL passed in so that we are able to reload the
   * iframe by updating the timestamp when the retry button is pressed in the timeout
   * screen.
   */
  get url(): string {
    try {
      const cacheBustingURL = new URL(this.args.url, this.windowLocation.href);

      // eslint-disable-next-line ember/use-ember-get-and-set
      cacheBustingURL.searchParams.set('_envoyts', this.timestamp.toString());

      return cacheBustingURL.toString();
    } catch (_) {
      return '';
    }
  }

  get loadingTimeout(): number {
    return this.args.loadingTimeout ?? 30000;
  }

  get resizeTimeout(): number {
    return this.args.resizeTimeout ?? 1000;
  }

  /**
   * iframe onLoad handler to signal that the iframe URL has successfully loaded.
   *
   * We use it to cancel out the spinner-then-error-screen task and start a new task that
   * will show a warning that the iframe was not correctly set up for iframeResizer if it
   * doesn't receive a proper resize event within a timeout.
   */
  @action
  onLoad(): void {
    void this.loadingIframeTask.cancelAll();
    this.state = IFRAME_STATE.LOADED;
    if (!config.isProduction && this.shouldWarnOnNoResize) {
      void this.showIframeWarningTask.perform();
    }
  }

  /**
   * When the caller passes `autoResize: false`, they're sizing the iframe
   * explicitly (e.g. full viewport via CSS) rather than relying on
   * iframe-resizer's height syncing. In that case `onResized` is never
   * called, and the "Check your iframe setup" debug banner would always
   * fire after `resizeTimeout` even though nothing is actually wrong.
   * Suppress it.
   */
  get shouldWarnOnNoResize(): boolean {
    const options = this.args.options as { autoResize?: boolean } | undefined;
    return options?.autoResize !== false;
  }

  /**
   * Callback passed to iframeResizer in the {{iframe-resize}} element modifier. Whenever
   * Garaje receives a message from a managed iframe, this handler will be invoked.
   */
  @action
  onMessage({ iframe, message }: MessageData): void {
    switch (message.event) {
      // sent by iframe when initialization is complete. we use this event to send the
      // initial context.
      case 'ready': {
        this.sendContext(iframe, [this.args.context]);
        break;
      }
      // sent by iframe when the user interacts with it, so garaje's inactivity timer
      // gets reset even though iframe events don't bubble to the parent window.
      case 'activity': {
        this.inactivity.handleActivity();
        break;
      }
      // sent by iframe when it is navigating to a garaje-owned URL. instead of following
      // the link inside the iframe, it delegates the navigation to garaje to perform a
      // native transition.
      // TODO: explore URL origin check to prevent navigation to external/unauthorized URLs
      case 'navigate': {
        if (message.url) {
          const { pathname, search } = new URL(message.url);
          this.router.transitionTo(`${pathname}${search}`);
        }
        break;
      }

      default:
        break;
    }
    this.messageBus.trigger(this.args.eventName ?? 'embedded-app-message', message);
  }

  /**
   * Callback fired by iframeResizer whenever the iframe resizes.
   *
   * We really only use this to tell if iframeResizer is set up correctly.
   */
  @action
  onResized({ height }: IframeResizeData): void {
    if (parseInt(height, 10)) {
      // if resize is successful, data will eventually contain a message with height > 0
      void this.showIframeWarningTask.cancelAll();
      // in the case it already showed the warning but took a while to call the onResized
      // callback
      this.showIframeWarning = false;
    }
  }

  /**
   * Create a cache-busting iframe src URL and restart the loading process.
   *
   * This action is attached the to button in the error screen that gets shown if the
   * iframe fails to load after `this.loadingTimeout`.
   */
  @action
  onRetry(): void {
    this.timestamp = Date.now();
    void this.loadingIframeTask.perform();
  }

  /**
   * Send context to the hosted iframe
   */
  @action
  sendContext(_: HTMLIFrameElement, [context]: [unknown]): void {
    if (context !== undefined) {
      const message: unknown = { event: 'context', context };
      // guard the call in case messageSender was never provided
      this.messageSender?.(message);
    }
  }

  @action
  registerMessageSender(element: HTMLElement & { iFrameResizer?: { sendMessage: (message: unknown) => void } }): void {
    this.messageSender = (message: unknown) => {
      element.iFrameResizer?.sendMessage(message);
    };
    this.embeddedApp.registerMessageSender(this.args.id, this.messageSender);
    registerDestructor(this, () => {
      // eslint-disable-next-line no-console
      console.log('cleanup message sender');
      this.embeddedApp.unregisterMessageSender(this.args.id);
    });
  }

  /**
   * Displays a loading spinner while the iframe has not yet emitted the onLoad event.
   * After the timeout, it will display an error. If the task is cancelled before the
   * timeout, the error will not display.
   *
   * NOTE: there doesn't seem to be a way to stop an iframe from loading, so even if the
   * state has transitioned to timeout, the iframe could very well emit the onLoad some
   * time after, which will move the state to loaded. The impact to the user is that they
   * could be staring at the error screen for a few seconds when suddenly the actual
   * the error screen goes away to be replaced by the iframe content.
   */
  loadingIframeTask = restartableTask(async (): Promise<void> => {
    this.state = IFRAME_STATE.LOADING;
    await timeout(this.loadingTimeout);
    this.state = IFRAME_STATE.TIMEOUT;
  });

  /**
   * Displays a helpful debugging message above the iframe when it detects that the iframe
   * is not set up correctly for autoresizing.
   */
  showIframeWarningTask = restartableTask(async (): Promise<void> => {
    this.showIframeWarning = false;
    await timeout(this.resizeTimeout);
    this.showIframeWarning = true;
  });

  <template>
    <div class="flex-1 flex flex-col" ...attributes>
      {{#if this.isLoading}}
        <div class="flex flex-1 items-center justify-center min-h-[200px]">
          <UiClassicLoadingSpinnerPulse class="!static" />
        </div>
      {{/if}}

      {{#if this.isTimedOut}}
        <div class="flex flex-col flex-1 items-center justify-center" data-test-timeout-error>
          <div class="animated fadeInUpSmall" style="max-width: 760px;">
            <div class="error-disconnected"></div>
            <h1 class="mt-8 text-center">Timed out</h1>
            <p class="my-4 text-center">
              The app took too long to load. Please try again or, if the problem persists,
              <a href="javascript:void(0);" onclick="Intercom('show')">contact support</a>.
            </p>
            <p class="mt-8 mb-0 text-center">
              <UiButton data-test-retry class="btn btn--small btn--brand" {{on "click" this.onRetry}}>
                Try again
              </UiButton>
            </p>
          </div>
        </div>
      {{/if}}

      {{#if this.showIframeWarning}}
        <UiClassicMessageBox @type="warning" class="mx-8 mt-3 mb-0" data-test-iframe-resizer-warning>
          <p class="m-0">
            Check your iframe setup. Ensure that the page is including
            <code>iFrameResizerContentWindow</code>
            and a
            <code>data-iframe-height</code>
            attribute exists.
          </p>
        </UiClassicMessageBox>
      {{/if}}

      <iframe
        title="embedded-app"
        class="border-none min-w-full {{unless this.isLoaded 'hidden'}} {{@iframeClass}}"
        src={{this.url}}
        scrolling={{if @scrolling "yes" "no"}}
        id={{@id}}
        {{iFrameResize this.onMessage this.onResized @options}}
        {{on "load" this.onLoad}}
        {{didUpdate this.sendContext @context}}
        {{didUpdate this.onRetry @url}}
        {{registerMessageSender this.registerMessageSender}}
        allow="clipboard-read; clipboard-write"
      />
    </div>
  </template>
}
